import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { analyticsServicesService } from "@/services/analytics-services-service";
import type { DateRange } from "@/hooks/useDateRange";
import { mapServicesListToTopRows } from "../components/services/servicesTabMappers";
import type { TopServicesFiltersState } from "../components/services/topServicesFilters";
import {
  buildServicesListQuery,
  resolveListSegmentCategory,
  type ServiceCategoryFilterOption,
} from "../components/services/topServicesListQuery";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";

export const analyticsServicesListKeys = {
  all: ["analytics-services-list"] as const,
  list: (query: ReturnType<typeof buildServicesListQuery>) =>
    [...analyticsServicesListKeys.all, query] as const,
};

export function useAnalyticsServicesList(params: {
  dateRange: DateRange;
  segmentParam: string | null;
  hasRapidPlan: boolean;
  filters: TopServicesFiltersState;
  nameSearch: string;
  page: number;
  pageSize: number;
  categoryOptions: ServiceCategoryFilterOption[];
}) {
  const rangeParams = useAnalyticsOverviewRange(params.dateRange);
  const segment = resolveListSegmentCategory(
    params.segmentParam,
    params.hasRapidPlan,
  );

  const listQuery = useMemo(
    () =>
      buildServicesListQuery({
        hotelId: rangeParams.hotelId,
        from: rangeParams.from,
        to: rangeParams.to,
        segmentParam: params.segmentParam,
        hasRapidPlan: params.hasRapidPlan,
        filters: params.filters,
        nameSearch: params.nameSearch,
        page: params.page,
        pageSize: params.pageSize,
        categoryOptions: params.categoryOptions,
      }),
    [
      params.categoryOptions,
      params.filters,
      params.hasRapidPlan,
      params.nameSearch,
      params.page,
      params.pageSize,
      params.segmentParam,
      rangeParams.from,
      rangeParams.hotelId,
      rangeParams.to,
    ],
  );

  const list = useQuery({
    queryKey: analyticsServicesListKeys.list(listQuery),
    queryFn: () => analyticsServicesService.getList(listQuery),
    enabled: rangeParams.isReady,
    placeholderData: (previous) => previous,
  });

  const rows = mapServicesListToTopRows(list.data, segment);
  const totalPages = Math.max(
    1,
    Math.ceil((list.data?.meta.total ?? 0) / params.pageSize),
  );

  return {
    ...rangeParams,
    listQuery,
    list,
    rows,
    total: list.data?.meta.total ?? 0,
    totalPages,
    isLoading: list.isLoading,
    isFetching: list.isFetching,
    isError: list.isError,
    error: list.error,
  };
}
